import React from "react";

// The shared vocabulary of the admin: page furniture, buttons, form fields and
// the loading / error / empty states. One file because these are all small and
// always used together — splitting them would cost more in imports than it
// saves in file length.

// ------------------------------ Page furniture ------------------------------

// The signature block. Every page opens the same way: an Archivo title over a
// court line. Repetition is the point — it is how the app is recognisable.
export const PageHeader = ({ title, subtitle, actions }) => (
  <header className="mb-8">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-ink">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
    <div className="court-rule mt-4" />
  </header>
);

export const Card = ({ children, className = "" }) => (
  <div className={`border border-sand bg-white ${className}`}>{children}</div>
);

// -------------------------------- Buttons -----------------------------------

const BUTTON_VARIANTS = {
  primary: "bg-teal text-white hover:bg-teal-dark border-teal hover:border-teal-dark",
  secondary: "bg-white text-ink hover:bg-teal-wash border-sand",
  danger: "bg-white text-danger hover:bg-danger-wash border-sand",
};

export const Button = ({
  variant = "secondary",
  type = "button",
  className = "",
  ...props
}) => (
  <button
    type={type}
    className={`inline-flex items-center gap-2 border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_VARIANTS[variant]} ${className}`}
    {...props}
  />
);

// --------------------------------- Forms ------------------------------------

const CONTROL =
  "w-full border border-sand bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-soft/60 focus:border-teal";

export const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-ink-soft">{hint}</span>}
  </label>
);

export const Input = ({ className = "", ...props }) => (
  <input className={`${CONTROL} ${className}`} {...props} />
);

export const Textarea = ({ className = "", ...props }) => (
  <textarea className={`${CONTROL} ${className}`} {...props} />
);

export const Select = ({ className = "", ...props }) => (
  <select className={`${CONTROL} cursor-pointer ${className}`} {...props} />
);

// --------------------------------- States -----------------------------------

export const Loader = ({ label = "Loading" }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-sand border-t-teal" />
    <p className="text-sm text-ink-soft">{label}</p>
  </div>
);

// Errors say what failed and what to do about it. No apologies.
export const ErrorState = ({ message = "That request didn't go through.", onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 border border-danger/30 bg-danger-wash py-12 text-center">
    <p className="text-sm font-medium text-danger">{message}</p>
    {onRetry && (
      <Button onClick={onRetry} variant="secondary">
        Try again
      </Button>
    )}
  </div>
);

// An empty screen is an invitation to act, so it carries the action.
export const EmptyState = ({ message, action }) => (
  <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
    <p className="text-sm text-ink-soft">{message}</p>
    {action}
  </div>
);

// --------------------------------- Badges -----------------------------------

const BADGE_TONES = {
  neutral: "bg-paper text-ink-soft border-sand",
  active: "bg-teal-wash text-teal-dark border-teal/30",
  pending: "bg-amber-wash text-amber border-amber/30",
  danger: "bg-danger-wash text-danger border-danger/30",
};

export const Badge = ({ tone = "neutral", children }) => (
  <span
    className={`inline-block border px-2 py-0.5 text-xs font-medium ${BADGE_TONES[tone]}`}
  >
    {children}
  </span>
);

// ---------------------------------- Table -----------------------------------

export const Table = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[40rem] border-collapse text-sm">
      <thead>
        <tr className="border-b border-sand text-left">
          {head.map((column) => (
            <th
              key={column.key ?? column.label}
              className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-soft ${
                column.align === "right" ? "text-right" : ""
              }`}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const Row = ({ children }) => (
  <tr className="border-b border-sand/60 last:border-0 hover:bg-teal-wash/40">
    {children}
  </tr>
);

export const Cell = ({ align, className = "", children }) => (
  <td
    className={`px-4 py-3 align-middle ${
      align === "right" ? "text-right" : ""
    } ${className}`}
  >
    {children}
  </td>
);
